import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  MessageCircle, Send, Loader2, Users, ImageIcon, CheckCircle2,
  AlertTriangle, Search, X, UserCheck, FileSpreadsheet, Upload, Download,
} from "lucide-react";
import { useAuth, API } from "../App";

/**
 * Admin → WhatsApp Broadcast card.
 *
 * Workflow:
 *  1. Admin clicks "Selecionar destinatários" → opens picker dialog
 *     with the list of confirmed-whatsapp employees (search + checkboxes).
 *  2. Selection feeds the broadcast: text + logo toggle + Disparar.
 *  3. Background job polled every 3s.
 */
const AdminWhatsappBroadcast = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [eligible, setEligible] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [text, setText] = useState("");
  const [includeLogo, setIncludeLogo] = useState(true);
  const [sending, setSending] = useState(false);

  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const xlsxInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const fetchEligible = useCallback(async () => {
    setLoadingEligible(true);
    try {
      const r = await axios.get(`${API}/admin/broadcast-whatsapp/eligible`, {
        headers,
        timeout: 15000,
      });
      setEligible(r.data.employees || []);
    } catch (err) {
      toast.error("Erro ao buscar funcionários elegíveis");
    } finally {
      setLoadingEligible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEligible();
  }, [fetchEligible]);

  // Poll job status while running
  useEffect(() => {
    if (!job?.job_id) return undefined;
    if (job.status === "done") {
      clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/broadcast-whatsapp/${job.job_id}`,
          { headers, timeout: 10000 },
        );
        setJob(r.data);
        if (r.data.status === "done") clearInterval(pollRef.current);
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  const filteredEligible = eligible.filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q)
    );
  });

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    const next = new Set(selectedIds);
    filteredEligible.forEach((e) => next.add(e.id));
    setSelectedIds(next);
  };

  const deselectAllFiltered = () => {
    const next = new Set(selectedIds);
    filteredEligible.forEach((e) => next.delete(e.id));
    setSelectedIds(next);
  };

  const allFilteredSelected =
    filteredEligible.length > 0 &&
    filteredEligible.every((e) => selectedIds.has(e.id));

  const handleImportXlsx = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/admin/employees/match-from-xlsx`, fd,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          timeout: 120000,
        },
      );
      const matched = r.data?.matched || [];
      const notFound = r.data?.not_found || [];

      // Only auto-select rows that are also eligible (whatsapp confirmed)
      const eligibleSet = new Set(eligible.map((e) => e.id));
      const matchedEligible = matched.filter((m) => eligibleSet.has(m.id));
      const matchedIneligible = matched.filter((m) => !eligibleSet.has(m.id));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        matchedEligible.forEach((m) => next.add(m.id));
        return next;
      });

      setImportReport({
        rows: r.data?.stats?.rows || 0,
        matched: matched.length,
        with_whatsapp: matchedEligible.length,
        without_whatsapp: matchedIneligible.length,
        not_found: notFound.length,
        not_found_items: notFound.slice(0, 50),
        ineligible_items: matchedIneligible.slice(0, 50),
      });

      if (matchedEligible.length) {
        toast.success(`${matchedEligible.length} funcionário(s) selecionado(s) da planilha`);
      } else if (matched.length) {
        toast.warning(
          `${matched.length} encontrado(s) na planilha, mas nenhum com WhatsApp confirmado.`,
        );
      } else {
        toast.error("Nenhum funcionário da planilha foi reconhecido.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao ler planilha. Verifique nome/CPF.",
      );
    } finally {
      setImporting(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = "";
    }
  };

  const handleSend = async () => {
    const message = text.trim();
    if (!message) {
      toast.error("Digite a mensagem antes de enviar.");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Selecione pelo menos um funcionário.");
      return;
    }
    if (!window.confirm(
      `Enviar esta mensagem para ${selectedIds.size} funcionário(s) selecionado(s)?`,
    )) return;

    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/broadcast-whatsapp`,
        {
          message,
          include_logo: includeLogo,
          employee_ids: Array.from(selectedIds),
        },
        { headers, timeout: 15000 },
      );
      toast.success(`Disparo iniciado para ${r.data.recipients} funcionário(s)`);
      setJob({
        job_id: r.data.job_id,
        status: "queued",
        sent: 0,
        failed: 0,
        total: r.data.recipients,
        errors: [],
      });
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar disparo.");
    } finally {
      setSending(false);
    }
  };

  const isRunning = job && job.status !== "done";
  const progress = job && job.total
    ? Math.min(100, Math.round(((job.sent + job.failed) / job.total) * 100))
    : 0;

  return (
    <Card
      className="bg-white border-slate-200 mt-4"
      data-testid="admin-whatsapp-broadcast-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center font-heading text-base">
          <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" />
          Disparo WhatsApp
        </CardTitle>
        <CardDescription className="text-xs flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          <span data-testid="broadcast-eligible-count">
            {loadingEligible ? "..." : eligible.length} elegível(is) ·{" "}
            <span className="font-semibold text-emerald-700" data-testid="broadcast-selected-count">
              {selectedIds.size}
            </span>{" "}
            selecionado(s)
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPickerOpen(true)}
              className="w-full h-9 text-xs justify-start"
              disabled={sending || isRunning}
              data-testid="broadcast-open-picker-btn"
            >
              <UserCheck className="w-3.5 h-3.5 mr-1.5" />
              Selecionar manualmente
              {selectedIds.size > 0 && (
                <Badge variant="outline" className="ml-auto bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                  {selectedIds.size}
                </Badge>
              )}
            </Button>

            <input
              ref={xlsxInputRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleImportXlsx(e.target.files?.[0])}
              data-testid="broadcast-xlsx-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => xlsxInputRef.current?.click()}
              disabled={importing || sending || isRunning}
              className="w-full h-9 text-xs justify-start border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50"
              data-testid="broadcast-import-xlsx-btn"
            >
              {importing
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-700" />}
              Importar planilha (.xlsx)
            </Button>
          </div>

          {/* Export the list of employees WITHOUT confirmed WhatsApp */}
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(
                  `${API}/admin/broadcast-whatsapp/missing-whatsapp.xlsx`,
                  { headers, responseType: "blob", timeout: 60000 },
                );
                const total = r.headers?.["x-total-rows"] || "?";
                const blob = new Blob([r.data], {
                  type:
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `avi_funcionarios_sem_whatsapp_${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success(`${total} funcionário(s) sem WhatsApp — planilha baixada`);
              } catch {
                toast.error("Falha ao gerar a planilha");
              }
            }}
            className="w-full h-9 text-xs justify-start border-amber-200 bg-amber-50/40 hover:bg-amber-50"
            data-testid="broadcast-export-missing-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1.5 text-amber-700" />
            Exportar lista de funcionários{" "}
            <span className="font-semibold mx-1">sem WhatsApp</span>
            <span className="ml-auto text-[10px] text-amber-700">
              (.xlsx)
            </span>
          </Button>

          {importReport && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-2.5 rounded-md border border-slate-200 bg-slate-50 text-[11px] space-y-1"
              data-testid="broadcast-import-report"
            >
              <div className="flex items-center justify-between font-medium text-slate-700">
                <span>
                  <FileSpreadsheet className="inline w-3.5 h-3.5 mr-1 text-emerald-700" />
                  Resultado da planilha
                </span>
                <button
                  type="button"
                  onClick={() => setImportReport(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1 text-slate-600">
                <span>📊 Linhas lidas:</span>
                <span className="text-right font-mono">{importReport.rows}</span>
                <span>✅ Selecionados (c/ WhatsApp):</span>
                <span className="text-right font-mono text-emerald-700 font-semibold">
                  {importReport.with_whatsapp}
                </span>
                {importReport.without_whatsapp > 0 && (
                  <>
                    <span>⏳ Sem WhatsApp confirmado:</span>
                    <span className="text-right font-mono text-amber-700">
                      {importReport.without_whatsapp}
                    </span>
                  </>
                )}
                {importReport.not_found > 0 && (
                  <>
                    <span>❌ Não encontrados:</span>
                    <span className="text-right font-mono text-red-700">
                      {importReport.not_found}
                    </span>
                  </>
                )}
              </div>
              {(importReport.not_found_items?.length > 0 ||
                importReport.ineligible_items?.length > 0) && (
                <details>
                  <summary className="cursor-pointer text-slate-500 mt-1">
                    Ver detalhes
                  </summary>
                  <ul className="mt-1 ml-4 list-disc max-h-32 overflow-y-auto space-y-0.5">
                    {importReport.not_found_items?.map((x, i) => (
                      <li key={`nf-${i}`} className="text-red-600">
                        Não encontrado: {x.nome_input || "—"} ({x.cpf_input || "sem CPF"})
                      </li>
                    ))}
                    {importReport.ineligible_items?.map((x, i) => (
                      <li key={`ie-${i}`} className="text-amber-700">
                        Sem WhatsApp: {x.nome} ({x.matricula})
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </motion.div>
          )}

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite a mensagem que será enviada via WhatsApp..."
            className="w-full h-28 p-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
            maxLength={1500}
            disabled={sending || isRunning}
            data-testid="broadcast-message-input"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer text-xs text-slate-600">
              <input
                type="checkbox"
                checked={includeLogo}
                onChange={(e) => setIncludeLogo(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                disabled={sending || isRunning}
                data-testid="broadcast-include-logo"
              />
              <ImageIcon className="w-3.5 h-3.5" />
              <span>Incluir logo da empresa</span>
            </label>
            <span className="text-xs text-slate-400">{text.length}/1500</span>
          </div>

          <Button
            onClick={handleSend}
            disabled={
              sending ||
              isRunning ||
              !text.trim() ||
              selectedIds.size === 0
            }
            className="w-full h-9 text-xs bg-emerald-600 hover:bg-emerald-700"
            data-testid="broadcast-send-btn"
          >
            {sending || isRunning ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1.5" />
            )}
            {isRunning
              ? "Enviando em background..."
              : `Disparar para ${selectedIds.size || "0"} selecionado(s)`}
          </Button>

          {job && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2"
              data-testid="broadcast-progress-panel"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-700">
                  {job.status === "done" ? "Finalizado" : "Em andamento"}
                </span>
                <Badge
                  variant="outline"
                  className={
                    job.status === "done"
                      ? "bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
                      : "bg-amber-100 text-amber-800 border-amber-200 text-[10px]"
                  }
                >
                  {job.status === "done" ? (
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                  ) : (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  )}
                  {job.sent + job.failed}/{job.total || 0}
                </Badge>
              </div>
              <Progress value={progress} className="h-2" />
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-1.5 bg-emerald-50 rounded text-center">
                  <p className="font-semibold text-emerald-700" data-testid="broadcast-sent">
                    {job.sent}
                  </p>
                  <p className="text-[10px] text-emerald-600">Enviadas</p>
                </div>
                <div className="p-1.5 bg-red-50 rounded text-center">
                  <p className="font-semibold text-red-700" data-testid="broadcast-failed">
                    {job.failed}
                  </p>
                  <p className="text-[10px] text-red-600">Falhas</p>
                </div>
              </div>
              {job.errors && job.errors.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-red-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Detalhes ({job.errors.length})
                  </summary>
                  <ul className="mt-1 max-h-32 overflow-y-auto space-y-1 pl-1">
                    {job.errors.map((e, i) => (
                      <li key={i} className="text-[10px] text-red-600">
                        <span className="font-mono">{e.matricula}</span>{" "}
                        {e.nome}: {e.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </motion.div>
          )}
        </div>
      </CardContent>

      {/* Recipients picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col"
          data-testid="broadcast-picker-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <UserCheck className="w-4 h-4 text-emerald-600" />
              Selecionar destinatários
            </DialogTitle>
            <DialogDescription className="text-xs">
              Marque os funcionários que receberão a mensagem.
              Apenas funcionários com WhatsApp confirmado aparecem aqui.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou matrícula..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="broadcast-picker-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
              className="h-9 text-xs whitespace-nowrap"
              data-testid="broadcast-picker-select-all"
            >
              <Checkbox checked={allFilteredSelected} className="mr-1.5" />
              {allFilteredSelected ? "Desmarcar" : "Marcar"} visíveis
            </Button>
          </div>

          <div className="text-xs text-slate-500 px-1 pb-2 flex items-center justify-between">
            <span>
              {filteredEligible.length} de {eligible.length} elegível(is)
            </span>
            <span className="text-emerald-700 font-medium">
              {selectedIds.size} selecionado(s)
            </span>
          </div>

          <div
            className="flex-1 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100"
            data-testid="broadcast-picker-list"
          >
            {loadingEligible ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : filteredEligible.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500">
                Nenhum funcionário encontrado.
              </div>
            ) : (
              filteredEligible.map((e) => {
                const checked = selectedIds.has(e.id);
                return (
                  <label
                    key={e.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                      checked ? "bg-emerald-50/50" : ""
                    }`}
                    data-testid={`broadcast-picker-row-${e.matricula}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleId(e.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {e.nome}
                      </p>
                      <p className="text-[11px] text-slate-500 font-mono">
                        Mat. {e.matricula} · {e.whatsapp}
                      </p>
                      {e.posto_servico && (
                        <p className="text-[10px] text-slate-400 truncate">
                          {e.posto_servico}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <DialogFooter className="gap-2 pt-3">
            <Button
              variant="outline"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
              data-testid="broadcast-picker-clear"
            >
              Limpar seleção
            </Button>
            <Button
              onClick={() => setPickerOpen(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="broadcast-picker-confirm"
            >
              Confirmar ({selectedIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminWhatsappBroadcast;
